import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, Navigate, Route, Routes } from 'react-router-dom';
import { FunctionalLoader } from 'seagull';
import { RequireVendorOnboardingComplete } from '~/components/RequireVendorOnboardingComplete/RequireVendorOnboardingComplete';
import { RouteSuspenseFallback } from '~/components/RouteSuspenseFallback/RouteSuspenseFallback';
import { RouteWrapper } from '~/components/RouteWrapper';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter/EventReporter';
import { useTeamInvites } from '~/hooks/reactQuery/queries/useTeamInvites';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { clearPersistedOnboardingProgress } from '~/pages/VendorOnboarding/hooks/useOnboardingFlow';
import { selectors } from '~/store/state-selectors';
import { safeNextParam } from '~/utils/auth-redirect';
import { getHomePagePages, prefetchAllRoutes } from '~/utils/routeConfigs';
import { withSplat } from '~/utils/routePaths';
import AcceptInvitationPage from '../AcceptInvitationPage/AcceptInvitationPage';
import { isUnknownHomeSubpath } from './MainPages.utils';
function Logout() {
    const { reset } = useStoreActions();
    const { sendEventToGtm } = useEventReporter();
    useEffect(()=>{
        sendEventToGtm('logout', {});
        clearPersistedOnboardingProgress();
        reset(true);
    }, []);
    return /*#__PURE__*/ _jsx(Navigate, {
        to: routes.home,
        replace: true
    });
}
export default function MainPages(props) {
    const { authorizedPages, unauthorizedPages, LoggedOutComponent } = props;
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const isVendor = useStore(selectors.user.isVendor);
    const hasBusiness = useStore(selectors.user.hasBusiness);
    const location = useLocation();
    const featureFlags = useFeatureFlags();
    const homeRouteUrls = useMemo(()=>getHomePagePages(featureFlags).map((config)=>config.url), [
        featureFlags
    ]);
    const { teamInvites, isLoading: isLoadingTeamInvites } = useTeamInvites();
    const [invitationPageDismissed, setInvitationPageDismissed] = useState(false);
    const showInvitationPage = !invitationPageDismissed && teamInvites.length > 0 && !hasBusiness;
    const setShowInvitationPage = (show)=>setInvitationPageDismissed(!show);
    const isResolvingInvitationRedirect = isLoadingTeamInvites && hasUserInfo && !hasBusiness;
    useEffect(()=>{
        prefetchAllRoutes();
    }, []);
    if (isResolvingInvitationRedirect) {
        return /*#__PURE__*/ _jsx(FunctionalLoader, {
            name: "team-invites-load",
            show: true
        });
    }
    const redirectToHome = ()=>{
        const nextPath = safeNextParam(location.search);
        if (nextPath != null) {
            return /*#__PURE__*/ _jsx(Navigate, {
                to: nextPath,
                replace: true
            });
        }
        return showInvitationPage ? /*#__PURE__*/ _jsx(Navigate, {
            to: {
                pathname: routes.acceptInvitation,
                search: location.search
            },
            replace: true
        }) : /*#__PURE__*/ _jsx(Navigate, {
            to: {
                pathname: routes.home,
                search: location.search
            },
            replace: true
        });
    };
    const renderAuthorizedElement = (config)=>{
        if (!hasUserInfo) {
            if (config.url === routes.home && isUnknownHomeSubpath(location.pathname, homeRouteUrls)) {
                return /*#__PURE__*/ _jsx(Navigate, {
                    to: routes.home,
                    replace: true
                });
            }
            return /*#__PURE__*/ _jsx(LoggedOutComponent, {});
        }
        return /*#__PURE__*/ _jsx(RequireVendorOnboardingComplete, {
            children: /*#__PURE__*/ _jsx(RouteWrapper, {
                config: config
            })
        });
    };
    return /*#__PURE__*/ _jsx(Suspense, {
        fallback: /*#__PURE__*/ _jsx(RouteSuspenseFallback, {}),
        children: /*#__PURE__*/ _jsxs(Routes, {
            children: [
                unauthorizedPages.map((config)=>/*#__PURE__*/ _jsx(Route, {
                        path: withSplat(config.url),
                        element: /*#__PURE__*/ _jsx(RouteWrapper, {
                            config: config
                        })
                    }, config.url)),
                showInvitationPage ? /*#__PURE__*/ _jsx(Route, {
                    path: withSplat(routes.acceptInvitation),
                    element: /*#__PURE__*/ _jsx(AcceptInvitationPage, {
                        setShowInvitationPage: setShowInvitationPage
                    })
                }) : authorizedPages.filter((param)=>{
                    let { vendorOnly } = param;
                    return !vendorOnly || isVendor;
                }).map((config)=>/*#__PURE__*/ _jsx(Route, {
                        path: withSplat(config.url),
                        element: renderAuthorizedElement(config)
                    }, config.url)),
                /*#__PURE__*/ _jsx(Route, {
                    path: routes.login,
                    element: redirectToHome()
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: routes.logout,
                    element: /*#__PURE__*/ _jsx(Logout, {})
                }),
                /*#__PURE__*/ _jsx(Route, {
                    path: "*",
                    element: redirectToHome()
                })
            ]
        })
    });
}
